/* eslint-disable react/no-unused-prop-types */
import {
    ReactNode, useCallback, useMemo,
} from 'react';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { useReduxForm } from '@rainbow-modules/hooks';
import { Option, RenderIf } from 'react-rainbow-components';
import Picklist from 'components/Picklist';
import useHttpQuery from 'data/firestore/useHttpQuery';

interface ElevenLabsAgent {
    id: string;
    name: string;
    createdAt: number;
}

interface ElevenLabsAgentPickerProps {
    id?: string;
    className?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    header?: string;
    hideHeader?: boolean;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

const ElevenLabsAgentPicker = (props: ElevenLabsAgentPickerProps) => {
    const {
        value: valueInProps,
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        header = 'Select voice pipeline',
        hideHeader = false,
        placeholder,
        error,
        required,
        disabled,
        readOnly,
    } = useReduxForm(props);

    const { data: response, isFetched } = useHttpQuery<{ data: ElevenLabsAgent[] }>({
        pathname: '/elevenlabs/agents',
    });

    const pickListvalue = useMemo(() => {
        if (valueInProps) {
            let inputValue: ElevenLabsAgent | null | undefined;
            if (typeof valueInProps === 'string') {
                inputValue = (response?.data || []).find((agent) => agent.id === valueInProps);
            }
            if (inputValue) {
                return {
                    label: inputValue.name,
                    name: inputValue.id,
                    value: inputValue.id,
                    searchableText: inputValue.name,
                };
            }
        }
        return undefined;
    }, [response, valueInProps]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => onChange(value.value),
        [onChange],
    );

    const pickListOptions = useMemo(
        () => (response?.data || []).map((agent) => (
            <Option
                key={`elevenlabs-agent-picker-option__${agent.id}`}
                label={agent.name}
                name={agent.id}
                value={agent.id}
                searchableText={agent.name}
            />
        )),
        [response],
    );

    return (
        <Picklist
            id={id}
            name={name}
            className={className}
            label={label}
            labelAlignment="left"
            tabIndex={tabIndex}
            required={required}
            placeholder={placeholder}
            value={pickListvalue}
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleOnChange}
            error={error}
            isLoading={!isFetched}
            borderRadius="semi-rounded"
        >
            <RenderIf isTrue={!hideHeader}>
                <Option label={header} variant="header" />
            </RenderIf>
            {pickListOptions}
        </Picklist>
    );
};

export default ElevenLabsAgentPicker;
